import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios'; 
import { jsx } from 'react/jsx-runtime';

export const AuthContext = createContext();

//Ensure `useContext(AuthContext)` doesn't break in SSR
export function getAuthContextSafe(context) {
  return context || {
    isAuthenticated: false,
    currentUser: null,
    setIsAuthenticated: () => {},
    setCurrentUser: () => {},
    logout: () => {},
    error: null,
    setError: () => {},
    NFTaccess: false,
    hasValidNFT: false,
  };
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); 
  const [error, setError] = useState(null);
  const [hasValidNFT, setHasValidNFT] = useState(false); 

  const logout = async (retries = 3) => {
    try {
      const response = await axios.post('/api/auth/logout', {}, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      if (response.status !== 200) {
        //console.warn(`Unexpected status during logout: ${response.status}`);
        setError('Logout did not complete successfully. Please try again.');
        return { success: false, message: 'Unexpected status' };
      }

      //console.log(`authcontext logout: ${response}`);
    } catch (error) {
      //console.error('Error during logout:', error);

      if (retries > 0) {
        //console.warn(`Retrying logout... (${3 - retries}/3 attempts left)`);
        await new Promise(res => setTimeout(res, 2000)); //Wait before retrying
        return await logout(retries - 1); //Recursively call logout with decremented retries
      }

      // Final error handling after retries are exhausted
      const isNetworkError = !error.response;
      const isUnauthorized = error.response?.status === 401;
      const errorMessage = isNetworkError
        ? 'Network error. Please check your connection and try again.'
        : isUnauthorized
        ? 'Your session has expired. Please log in again.'
        : 'An unexpected error occurred during logout. Please try again.';

      setError(errorMessage);
    } finally {
      //Ensure localStorage items are always removed, even if logout fails
      localStorage.removeItem('username');
      localStorage.removeItem('hasValidNFT');
      localStorage.removeItem('userAddress');

      //Update React state to reflect logout
      setIsAuthenticated(false);
      setCurrentUser(null);
    }

    return { success: true };
  };


  const NFTaccess = async () => {

    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

        const userAddress = accounts[0];
        const network = localStorage.getItem('selectedNetwork') || 'mainnet';

        const response = await axios.post('/api/check-nft-ownership-by-address', { userAddress, network });

        const hasValidNFT = response.data.hasValidNFT;
        //console.log("NFT Ownership Check:", hasValidNFT);

        if (hasValidNFT) {
          setIsAuthenticated(true);  
          setHasValidNFT (true);  
          setCurrentUser(userAddress);   
          localStorage.setItem('userAddress', userAddress); 
          localStorage.setItem('username', userAddress); 
          localStorage.setItem('hasValidNFT', 'true');
        }
      } catch (error) {
        //console.error("Error in NFTaccess:", error);
        localStorage.removeItem('userAddress'); 
        localStorage.removeItem('username'); 
        localStorage.removeItem('selectedNetwork');
        localStorage.removeItem('hasValidNFT');
      }
    } else {
        setError("MetaMask not found. Please install MetaMask to continue.");
    }
  };

  const refresh = async (retries = 3) => {
    try {
      setError(null);

      const response = await axios.post('/api/auth/refresh', {}, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      //console.log("Token refresh successful.");

    } catch (error) {
      //console.error('Error occurred while refreshing token:', error);

      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        //console.log("Refresh token is invalid. Logging out...");
        await logout(); 
        setIsAuthenticated(false);
        setCurrentUser(null);
        setError('Please login again');
        localStorage.removeItem('username');
        return null; 
      }

      // Retry logic for transient errors
      if (retries > 0) {
        //console.log(`Retrying token refresh... attempts left: ${retries}`);
        await new Promise(resolve => setTimeout(resolve, 200)); 
        return await refresh(retries - 1);
      }

      //console.error('Token refresh failed after retries.');
      setError('Token refresh failed after maximum retries.');
      return null; // Return fallback value
    }
  };

// Function to refresh authentication & check user status
const updateAuthentication = async () => {
  const username = localStorage.getItem("username");
  const userAddress = localStorage.getItem("userAddress");

  try {
    if (username) {
      // **JWT-based authentication (for normal registered users)**
      await refresh(); // Calls API to refresh access token
      setIsAuthenticated(true);
      setCurrentUser(username);
      //console.log(`[updateAuthentication] Authenticated as: ${username}`);
      return;
    }

    if (userAddress) {
      // **Web3 users (Only has a wallet address, no JWT)**
      setIsAuthenticated(true);
      setCurrentUser(userAddress);
      //console.log(`[updateAuthentication] Authenticated with public address: ${userAddress}`);
      return;
    }

    // If neither username nor userAddress is present, reset authentication state
    //console.warn("[updateAuthentication] No valid authentication found. Resetting state.");
    setIsAuthenticated(false);
    setCurrentUser(null);

  } catch (error) {
    //console.error("[updateAuthentication] Authentication refresh error:", error);
    setIsAuthenticated(false);
    setCurrentUser(null);
    setError(error.message || "Authentication refresh failed");
  }
};


//Load authentication on page load
useEffect(() => {
  updateAuthentication();
}, []);

//Periodic refresh every 14 minutes
useEffect(() => {
  const refreshInterval = setInterval(() => {
    updateAuthentication();
  }, 55 * 60 * 1000);

  return () => clearInterval(refreshInterval);
}, [isAuthenticated]);


return jsx(AuthContext.Provider, {
  value: {
    isAuthenticated,
    setIsAuthenticated,
    currentUser,
    setCurrentUser,
    logout,
    error,
    setError,
    NFTaccess,
    hasValidNFT
  },
  children: children
});


}

